import { useEffect, useState } from "react";
import { LogOut, Pencil, Plus, Trash2, UploadCloud, X } from "lucide-react";
import Logo from "../components/Logo";
import { useLang } from "../i18n/LanguageContext";
import { api, authConfig, getToken, imgSrc, setToken } from "../lib/api";

const EMPTY = {
  brand: "", model: "", year: "", km: "", price: "", fuel: "petrol", transmission: "automatic",
  status: "used", description: "", images: [], featured: false, sold: false,
};

export default function Admin() {
  const { t } = useLang();
  const [token, setHasToken] = useState(!!getToken());
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [cars, setCars] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");

  const loadCars = () => api.get("/cars").then((r) => setCars(r.data)).catch(() => {});

  useEffect(() => {
    if (token) loadCars();
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await api.post("/auth/login", { password });
      setToken(data.token);
      setHasToken(true);
    } catch {
      setError(t("admin.wrong"));
    }
  };

  const logout = () => {
    setToken(null);
    setHasToken(false);
    setToken(null);
  };

  const openEdit = (car) => {
    setForm({ ...EMPTY, ...car, images: car.images || [] });
    setEditing(car.id);
    setFormError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const openNew = () => {
    setForm(EMPTY);
    setEditing("new");
    setFormError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const closeForm = () => {
    setEditing(null);
    setForm(EMPTY);
  };

  const uploadFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    setFormError("");
    try {
      const uploaded = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, authConfig());
        uploaded.push(data.url);
      }
      setForm((f) => ({ ...f, images: [...f.images, ...uploaded] }));
    } catch {
      setFormError(t("admin.uploadFail"));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeImage = (url) => setForm((f) => ({ ...f, images: f.images.filter((i) => i !== url) }));

  const save = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!form.brand || !form.model || !form.year || form.price === "") {
      setFormError(t("admin.required"));
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      year: Number(form.year),
      km: Number(form.km || 0),
      price: Number(form.price),
    };
    try {
      if (editing === "new") await api.post("/cars", payload, authConfig());
      else await api.put(`/cars/${editing}`, payload, authConfig());
      closeForm();
      loadCars();
    } catch {
      setFormError(t("admin.uploadFail"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (car) => {
    if (!window.confirm(t("admin.confirmDelete"))) return;
    await api.delete(`/cars/${car.id}`, authConfig()).catch(() => {});
    loadCars();
  };

  if (!token)
    return (
      <main className="min-h-screen bg-paper2 flex items-center justify-center px-6" data-testid="admin-login-page">
        <form onSubmit={login} className="w-full max-w-sm bg-paper border border-ink/10 p-10" data-testid="admin-login-form">
          <div className="mb-8"><Logo /></div>
          <h1 className="display-title text-3xl mb-8">{t("admin.title")}</h1>
          <label className="field-label" htmlFor="admin-password">{t("admin.password")}</label>
          <input
            id="admin-password"
            type="password"
            data-testid="admin-password-input"
            className="field mb-4"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
          />
          {error && <p className="text-rosso text-sm mb-4" data-testid="admin-login-error">{error}</p>}
          <button type="submit" className="btn-solid w-full justify-center" data-testid="admin-login-button">
            {t("admin.login")}
          </button>
        </form>
      </main>
    );

  return (
    <main className="min-h-screen bg-paper2 pt-10 pb-24" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-10">
          <div className="flex items-center gap-4">
            <Logo />
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-fog">{t("admin.title")}</span>
          </div>
          <div className="flex gap-3">
            <button onClick={openNew} className="btn-solid" data-testid="admin-add-car">
              <Plus size={15} />
              {t("admin.add")}
            </button>
            <button onClick={logout} className="inline-flex items-center gap-2 border border-ink/20 px-5 py-3 text-sm uppercase tracking-[0.14em] hover:border-rosso hover:text-rosso transition-colors" data-testid="admin-logout">
              <LogOut size={15} />
              {t("admin.logout")}
            </button>
          </div>
        </div>

        {editing && (
          <form onSubmit={save} className="bg-paper border border-ink/10 p-6 md:p-8 mb-10" data-testid="admin-car-form">
            <h2 className="display-title text-3xl mb-6">{editing === "new" ? t("admin.add") : t("admin.edit")}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="field-label" htmlFor="car-brand">{t("f.brand")} *</label>
                <input id="car-brand" data-testid="car-form-brand" className="field" value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} />
              </div>
              <div>
                <label className="field-label" htmlFor="car-model">{t("f.model")} *</label>
                <input id="car-model" data-testid="car-form-model" className="field" value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} />
              </div>
              <div>
                <label className="field-label" htmlFor="car-year">{t("f.year")} *</label>
                <input id="car-year" data-testid="car-form-year" type="number" className="field" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} />
              </div>
              <div>
                <label className="field-label" htmlFor="car-km">{t("f.km")}</label>
                <input id="car-km" data-testid="car-form-km" type="number" className="field" value={form.km} onChange={(e) => setForm({ ...form, km: e.target.value })} />
              </div>
              <div>
                <label className="field-label" htmlFor="car-price">{t("f.price")} *</label>
                <input id="car-price" data-testid="car-form-price" type="number" className="field" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
              </div>
              <div>
                <label className="field-label" htmlFor="car-status">{t("f.status")}</label>
                <select id="car-status" data-testid="car-form-status" className="field" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                  <option value="new">{t("status.new")}</option>
                  <option value="used">{t("status.used")}</option>
                </select>
              </div>
              <div>
                <label className="field-label" htmlFor="car-fuel">{t("f.fuel")}</label>
                <select id="car-fuel" data-testid="car-form-fuel" className="field" value={form.fuel} onChange={(e) => setForm({ ...form, fuel: e.target.value })}>
                  <option value="petrol">{t("fuel.petrol")}</option>
                  <option value="diesel">{t("fuel.diesel")}</option>
                  <option value="hybrid">{t("fuel.hybrid")}</option>
                  <option value="electric">{t("fuel.electric")}</option>
                  <option value="lpg">{t("fuel.lpg")}</option>
                </select>
              </div>
              <div>
                <label className="field-label" htmlFor="car-transmission">{t("f.transmission")}</label>
                <select id="car-transmission" data-testid="car-form-transmission" className="field" value={form.transmission} onChange={(e) => setForm({ ...form, transmission: e.target.value })}>
                  <option value="manual">{t("tr.manual")}</option>
                  <option value="automatic">{t("tr.automatic")}</option>
                </select>
              </div>
              <div className="flex items-end gap-6 pb-2">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" data-testid="car-form-featured" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} />
                  {t("admin.featured")}
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" data-testid="car-form-sold" checked={form.sold} onChange={(e) => setForm({ ...form, sold: e.target.checked })} />
                  {t("admin.sold")}
                </label>
              </div>
            </div>

            <div className="mt-5">
              <label className="field-label" htmlFor="car-description">{t("f.description")} <span className="normal-case">({t("admin.optional")})</span></label>
              <textarea id="car-description" data-testid="car-form-description" rows={3} className="field" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>

            <div className="mt-6">
              <p className="field-label">{t("admin.photos")}</p>
              <label
                htmlFor="car-photos"
                className={`inline-flex items-center gap-3 border border-dashed border-ink/30 px-6 py-4 text-sm cursor-pointer hover:border-blu transition-colors ${uploading ? "opacity-50 pointer-events-none" : ""}`}
                data-testid="car-photos-upload"
              >
                <UploadCloud size={18} />
                {uploading ? t("admin.uploading") : t("admin.uploadHint")}
              </label>
              <input id="car-photos" type="file" accept="image/*" multiple className="hidden" onChange={uploadFiles} />
              {form.images.length > 0 && (
                <div className="flex flex-wrap gap-3 mt-4" data-testid="car-photos-preview">
                  {form.images.map((url) => (
                    <div key={url} className="relative w-24 h-16 overflow-hidden border border-ink/10">
                      <img src={imgSrc(url)} alt="" className="w-full h-full object-cover" />
                      <button type="button" onClick={() => removeImage(url)} className="absolute top-1 right-1 bg-white/90 p-0.5 hover:text-rosso" data-testid="car-photo-remove">
                        <X size={13} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {formError && <p className="text-rosso text-sm mt-4" data-testid="admin-form-error">{formError}</p>}

            <div className="flex gap-3 mt-8">
              <button type="submit" disabled={saving || uploading} className="btn-solid disabled:opacity-50" data-testid="admin-save-car">
                {saving ? t("admin.saving") : t("admin.save")}
              </button>
              <button type="button" onClick={closeForm} className="inline-flex items-center border border-ink/20 px-6 py-3.5 text-sm uppercase tracking-[0.14em] hover:border-rosso hover:text-rosso transition-colors" data-testid="admin-cancel-car">
                {t("admin.cancel")}
              </button>
            </div>
          </form>
        )}

        <h2 className="eyebrow mb-5">{t("admin.cars")} — {cars.length}</h2>
        {cars.length === 0 ? (
          <p className="text-fog py-16 text-center font-display italic text-2xl">{t("admin.empty")}</p>
        ) : (
          <div className="bg-paper border border-ink/10 divide-y divide-ink/5" data-testid="admin-cars-table">
            {cars.map((car) => (
              <div key={car.id} className="flex items-center gap-4 p-4" data-testid={`admin-car-row-${car.id}`}>
                <div className="w-20 h-14 flex-none overflow-hidden bg-paper3">
                  {car.images?.[0] && <img src={imgSrc(car.images[0])} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-display text-xl truncate">{car.brand} {car.model}</p>
                  <p className="font-mono text-[11px] text-fog tracking-[0.1em] uppercase">
                    {car.year} · {t(`status.${car.status}`) || car.status}
                    {car.featured ? ` · ${t("admin.featured")}` : ""}
                    {car.sold ? ` · ${t("admin.sold")}` : ""}
                  </p>
                </div>
                <button onClick={() => openEdit(car)} className="p-2 hover:text-blu transition-colors" aria-label={t("admin.edit")} data-testid={`admin-edit-${car.id}`}>
                  <Pencil size={17} />
                </button>
                <button onClick={() => remove(car)} className="p-2 hover:text-rosso transition-colors" aria-label={t("admin.delete")} data-testid={`admin-delete-${car.id}`}>
                  <Trash2 size={17} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
